import {useState, useEffect} from 'react';
import moment from 'moment';

import * as UserActions from '../store/actions/user';
import {useDispatch, useSelector} from 'react-redux';
// eslint-disable-next-line max-len
import {resetRecommendations} from '../store/actions/recommendation';
import {Auth} from 'aws-amplify';
import {useLocation} from 'react-router';

export const useGetUserToken = () => {
    const dispatch = useDispatch();
    const user = useSelector(state => state.user);

    const [token, setToken] = useState(undefined);

    useEffect(() => {
        if (user.jwtToken) {
            let decodedToken = JSON.parse(atob(user.jwtToken.split('.')[1]));

            let now = Date.now() / 1000;

            if (decodedToken.exp <= now) {
                try {
                    const refreshToken = async () => {
                        // eslint-disable-next-line max-len
                        const authenticatedUser = await Auth.currentAuthenticatedUser();
                        const currentSession =
                            authenticatedUser.signInUserSession;

                        authenticatedUser.refreshSession(
                            currentSession.refreshToken,
                            (err, session) => {
                                if (!err && session) {
                                    UserActions.login({
                                        jwtToken: session
                                            .getIdToken()
                                            .getJwtToken(),
                                    });

                                    setToken(
                                        session.getIdToken().getJwtToken(),
                                    );
                                }
                            },
                        );
                    };

                    refreshToken();
                } catch (e) {
                    // eslint-disable-next-line
                    console.error('Unable to refresh Token', e);
                    dispatch(resetRecommendations);
                    dispatch(UserActions.logout());

                    setToken(false);
                }
            } else {
                setToken(user.jwtToken);
            }
        } else {
            setToken(undefined);
        }

        // eslint-disable-next-line
    }, [user]);

    return token;
};

export const formatContentDuration = duration => {
    if (!duration || duration <= 0) {
        return null;
    }

    let format = 'H[h] mm[m]';

    if (duration / 60 < 1) {
        format = 'ss[s]';
    } else if (duration / 60 < 60) {
        format = 'm[m]';
    }

    return moment()
        .startOf('day')
        .seconds(duration)
        .format(format);
};

export const contentUrlSolver = (
    config,
    contentId,
    imageType = 'SHOW-SELECTOR',
    fileType = 'jpg',
    additionalParams = undefined,
) => {
    if (config.images && config.images.endpoint && config.images.urlTemplate) {
        return `${config.images.endpoint}${(imageType === 'THUMBNAIL'
            ? config.images.thumbs
            : config.images.urlTemplate
        )
            .replace(/<contentId>/g, contentId)
            .replace(/<imageType>/g, imageType)
            .replace('<[jpg,png]>', fileType)}${additionalParams || ''}`;
    }
    return null;
};

export const capitalize = function(string, all = false) {
    string = string.toLowerCase();

    if (!all) return string.charAt(0).toUpperCase() + string.slice(1);
    else
        return string
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
};

// eslint-disable-next-line max-len
const mobileRE = /(android|bb\d+|meego).+mobile|armv7l|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series[46]0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i;
const tabletRE = /android|ipad|playbook|silk/i;

export const isiOS = () => {
    return (
        (navigator.userAgent.includes('Mac') && 'ontouchend' in document) ||
        [
            'iPad Simulator',
            'iPhone Simulator',
            'iPod Simulator',
            'iPad',
            'iPhone',
            'iPod',
        ].includes(navigator.platform)
    );
};

export const isMobileDevice = opts => {
    if (!opts) opts = {tablet: true, featureDetect: true};
    let ua = opts.ua;
    if (!ua && typeof navigator !== 'undefined') ua = navigator.userAgent;
    if (ua && ua.headers && typeof ua.headers['user-agent'] === 'string') {
        ua = ua.headers['user-agent'];
    }
    if (typeof ua !== 'string') return false;

    let result = mobileRE.test(ua) || (!!opts.tablet && tabletRE.test(ua));

    if (
        !result &&
        opts.tablet &&
        opts.featureDetect &&
        navigator &&
        navigator.maxTouchPoints > 1 &&
        ua.indexOf('Macintosh') !== -1 &&
        ua.indexOf('Safari') !== -1
    ) {
        result = true;
    }

    return result;
};

export const slugify = str => {
    str = str.replace(/^\s+|\s+$/g, ''); // trim
    str = str.toLowerCase();
    // remove accents, swap ñ for n, etc
    var from = 'ãàáäâèéëêìíïîòóöôùúüûñç·/_,:;';
    var to = 'aaaaaeeeeiiiioooouuuunc------';
    for (var i = 0, l = from.length; i < l; i++) {
        str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
    }
    str = str
        .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
        .replace(/\s+/g, '-') // collapse whitespace and replace by -
        .replace(/-+/g, '-'); // collapse dashes
    return str;
};

export const isOffscreen = function(element) {
    if (!element) return null;

    var elementBoundings = element.getBoundingClientRect();

    var offScreen =
        elementBoundings.top + elementBoundings.height / 2 < 0 ||
        elementBoundings.left < 0 ||
        elementBoundings.bottom - elementBoundings.height / 2 >
            (window.innerHeight || document.documentElement.clientHeight) ||
        elementBoundings.right >
            (window.innerWidth || document.documentElement.clientWidth);

    return offScreen;
};

export const currentEnv =
    (process.env.REACT_APP_STAGE || 'dev') === 'prod'
        ? 'prd'
        : process.env.REACT_APP_STAGE || 'dev';

export const filterLives = (availableLives, userLocation, tve) => {
    let channelName = 'CNPLUS_LATAM_PNR';

    switch (userLocation) {
        case 'AR':
        case 'UY':
        case 'PY':
            channelName = 'CNPLUS_LATAM_ARG';
            break;
        case 'MX':
            channelName = 'CNPLUS_LATAM_MEX';
            break;
        case 'CL':
        case 'PE':
        case 'BO':
            channelName = 'CNPLUS_LATAM_PNR2';
            break;
        case 'BR':
            channelName = 'CNPLUS_LATAM_BR';
            break;
        default:
            channelName = 'CNPLUS_LATAM_PNR';
    }

    let filteredLives = [
        ...availableLives.filter(live => {
            if (isLiveChannel(live) && !tve) return false;
            return live.description === channelName;
        }),
    ];

    return filteredLives;
};

const isLiveChannel = video => {
    //we can only compare by name right now, so lets use that...
    const liveChannels = [
        'CNPLUS_LATAM_ARG',
        'CNPLUS_LATAM_MEX',
        'CNPLUS_LATAM_PNR',
        'CNPLUS_LATAM_PNR2',
        'CNPLUS_LATAM_BR',
    ];

    return liveChannels.indexOf(video.description) !== -1;
};

export const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

export const useIsCartoonito = () => {
    const search = useQuery();

    return (
        window.location.hostname.indexOf('cartoonito') >= 0 ||
        (currentEnv !== 'prd' && search.get('cnito') === 'true')
    );
};

export const debugCNitoUrl = () => {
    let search = new URLSearchParams(window.location.search);

    if (currentEnv !== 'prd' && search.get('cnito') === 'true') {
        return '?cnito=true';
    }
    return '';
};

export const isCnito = () => {
    let search = new URLSearchParams(window.location.search);

    return (
        window.location.hostname.indexOf('cartoonito') >= 0 ||
        (currentEnv !== 'prd' && search.get('cnito') === 'true')
    );
};

export const preloadImage = url => {
    const img = new Image();
    img.src = url;
    // use img.onload to support a callback
};
